/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { createSelector } from '@reduxjs/toolkit';
import _ from 'lodash';

import { selectDraftEnvironmentId } from '@ibm-watson/shared/modules/ia/store/environments/selectors';
import { isCpd, isWxoEnabled } from '@ibm-watson/shared/modules/ia/util/config';
import { ApiStatus } from '@ibm-watson/shared/ts';
import {
  IntegrationSchema,
  IntegrationSettings
} from '@ibm-watson/shared/ts/integration';
import type { RootState } from '@ibm-watson/shared/ts/state';
import { ParsedOpenAPIExtension } from '../../../../ts/extensions';
import {
  isPhoneWxoEnabled,
  selectIsGenesysAudioConnectorInCpd
} from '../../../ia/store/features/selectors';
import {
  channelCardIntegrations,
  CATALOG_CHANNEL_TYPE,
  PHONE_CHANNEL_TYPE,
  WEBCHAT_CHANNEL_TYPE,
  SMS_CHANNEL_TYPE,
  SMS_CHANNEL_TYPES
} from '../../util/channelTypes';
import { extensionsAdapter, integrationsAdapter } from './adapter';

const integrationSelectors = integrationsAdapter.getSelectors(
  (state: RootState) => state.integrations
);
const extensionSelectors = extensionsAdapter.getSelectors(
  (state: RootState) => state.parsedOpenAPIExtensions?.instances
);

export const selectAllIntegrations = integrationSelectors.selectAll;
export const selectIntegrationById = integrationSelectors.selectById;
export const selectAllExtensions = extensionSelectors.selectAll;

export const selectSavedDraftIntegration = (
  state: RootState
): IntegrationSchema | null =>
  selectIntegrationById(
    state,
    state.integrations?.draftIntegration?.integration?.integration_id
  );

export const selectIntegrationsByType = (
  state: RootState,
  channelType: string
): IntegrationSchema[] =>
  selectAllIntegrations(state).filter(
    integration =>
      integration.type === channelType ||
      (channelType === SMS_CHANNEL_TYPE &&
        SMS_CHANNEL_TYPES.includes(integration.type))
  );

export const selectExtensionIntegrations = (
  state: RootState
): ParsedOpenAPIExtension[] =>
  selectAllExtensions(state).filter(
    integration => integration?.type === CATALOG_CHANNEL_TYPE
  );

export const selectFirstIntegrationIdByType = (
  state: RootState,
  channelType: string,
  environmentId: string,
  catalogItemId?: string
): string | null => {
  const integrations = selectAllIntegrations(state).filter(
    integration =>
      integration.environment_id === environmentId &&
      (integration.type === channelType ||
        (channelType === SMS_CHANNEL_TYPE &&
          SMS_CHANNEL_TYPES.includes(integration.type)))
  );

  if (channelType === CATALOG_CHANNEL_TYPE) {
    return integrations.find(
      integration => integration?.settings?.catalog_item_id === catalogItemId
    )?.integration_id;
  }

  return integrations[0]?.integration_id || null;
};

export const selectAllDraftIntegrations = createSelector(
  [selectAllIntegrations, selectDraftEnvironmentId],
  (integrations, environmentId) =>
    integrations?.filter(
      integration => integration.environment_id === environmentId
    ) || []
);

export const selectDraftIntegration = (
  state: RootState
): IntegrationSchema | null =>
  state.integrations?.draftIntegration?.integration;

export const selectDraftIntegrationId = (state: RootState): string =>
  state.integrations?.draftIntegration?.integration?.integration_id;

export const selectDraftIntegrationSettings = (
  state: RootState
): IntegrationSettings =>
  state.integrations?.draftIntegration?.integration?.settings;

export const selectDraftIntegrationPrivateFieldsEnabled = (state: RootState) =>
  state.integrations?.draftIntegration?.enable_secret_fields;

export const selectCurrentIntegrationId = (state: RootState): string =>
  state.integrations?.currentIntegrationId;

export const isIntegrationLoading = (state: RootState): boolean =>
  state.integrations?.loadIntegration?.status === ApiStatus.LOADING;

export const isIntegrationLoaded = (state: RootState): boolean =>
  state.integrations?.loadIntegration?.status === ApiStatus.SUCCEEDED;

export const areIntegrationsLoaded = (state: RootState): boolean =>
  state.integrations?.listIntegrations?.status === ApiStatus.SUCCEEDED;

export const areIntegrationsLoading = (state: RootState): boolean =>
  state.integrations?.listIntegrations?.status === ApiStatus.LOADING;

export const selectListIntegrationsPendingCallStatus = (state: RootState) =>
  state.integrations?.listIntegrations?.pendingCallStatus;

export const isUpdatingIntegration = (state: RootState): boolean =>
  state.integrations?.updateIntegration?.status === ApiStatus.LOADING;

export const isCreatingIntegration = (state: RootState): boolean =>
  state.integrations?.createIntegration?.status === ApiStatus.LOADING;

export const selectSubscribeStatus = (state: RootState): ApiStatus =>
  state.integrations?.subscribeIntegration?.status;

export const isSubscribeIntegrationLoading = (state: RootState): boolean =>
  state.integrations?.subscribeIntegration?.status === ApiStatus.LOADING;

export const didSubscribeIntegrationSucceed = (state: RootState): boolean =>
  state.integrations?.subscribeIntegration?.status === ApiStatus.SUCCEEDED;

export const didSubscribeIntegrationFail = (state: RootState): boolean =>
  state.integrations?.subscribeIntegration?.status === ApiStatus.ERROR;

export const selectIsIntegrationPrematurelyCreated = (
  state: RootState
): boolean =>
  state.integrations?.draftIntegration?.integration_prematurely_created;

export const selectIsIntegrationDirty = (state: RootState): boolean =>
  state.integrations?.draftIntegration?.name?.dirty ||
  state.integrations?.draftIntegration?.settings?.dirty;

export const selectIsIntegrationValid = (state: RootState): boolean =>
  state.integrations?.draftIntegration?.name?.valid &&
  state.integrations?.draftIntegration?.settings?.valid;

export const selectIsIntegrationInitializing = (state: RootState): boolean =>
  state.integrations?.draftIntegration?.integration_initializing;

/**
 * Compares the state of the current integration to the saved integration.
 * If they are equal, there have been no changes and save and exit should be disabled.
 * If they are not equal, there have been changes and save and exit should enable.
 */
export const selectIntegrationHasChanges = (state: RootState): boolean => {
  const stateDraftIntegration =
    state.integrations?.draftIntegration?.integration;
  const savedDraftIntegration = selectSavedDraftIntegration(state);
  return !_.isEqual(stateDraftIntegration, savedDraftIntegration);
};

export const selectIsSaveCreateButtonDisabled = (state: RootState): boolean =>
  state.integrations?.draftIntegration?.disable_save_create_button;

/**
 * By the last tab of a ChannelOverlay, this selector determines
 * whether the Save and Exit button is enabled. The rule for enabling
 * this button is generally: IF the integration is dirty AND valid.
 * This button should also be disabled if the integration has no changes
 * from its saved state, even if it is dirty.
 *
 * However, prematurely created integrations (i.e. if the
 * integration had to be created BEFORE the user finishes the
 * ChannelOverlay) will cause dirty to be false and force the button
 * to be disabled. Hence, the dirty condition is replaced with
 * (dirty || integrationIsPrematurelyCreated).
 */
export const selectShouldSaveIntegration = (state: RootState): boolean =>
  selectIsIntegrationValid(state) &&
  (selectIntegrationHasChanges(state) ||
    selectIsIntegrationDirty(state) ||
    selectIsIntegrationPrematurelyCreated(state));

/**
 * Select all integrations in the draft environment that we represent as ui cards.
 *
 */
export const selectIntegrationsUsingChannelCards = createSelector(
  [selectAllIntegrations, selectDraftEnvironmentId],
  (integrations, environmentId) =>
    integrations?.filter(
      integration =>
        integration.environment_id === environmentId &&
        channelCardIntegrations.includes(integration.type)
    ) ?? []
);

/**
 *
 */
export const selectFeaturedChannels = (state: RootState): string[] => {
  const featuredChannels = [WEBCHAT_CHANNEL_TYPE];

  const isGenesysAudioConnectorEnabledInCpd =
    selectIsGenesysAudioConnectorInCpd(state);

  // If the Genesys audio connector is enabled in CPD 5+, we enable the
  // Phone channel ONLY in WXO; Phone should not otherwise be enabled in CPD.
  // Phone is usually enabled in SaaS environments (but there is a flag that
  // can decide whether or not to enable it).
  if (
    (isGenesysAudioConnectorEnabledInCpd && isWxoEnabled) ||
    (!isCpd && isPhoneWxoEnabled(state))
  ) {
    featuredChannels.push(PHONE_CHANNEL_TYPE);
  }
  return featuredChannels;
};
